import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  jsonLd?: Record<string, unknown>[];
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  ogType,
  twitterCard,
  twitterTitle,
  twitterDescription,
  jsonLd,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (attrName: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el: HTMLMetaElement | null = document.querySelector(`meta[${attr}="${attrName}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrName);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (ogTitle) setMeta('og:title', ogTitle, true);
    if (ogDescription) setMeta('og:description', ogDescription, true);
    if (ogUrl) setMeta('og:url', ogUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);
    if (ogType) setMeta('og:type', ogType, true);
    if (twitterCard) setMeta('twitter:card', twitterCard);
    if (twitterTitle) setMeta('twitter:title', twitterTitle);
    if (twitterDescription) setMeta('twitter:description', twitterDescription);

    if (canonicalUrl) {
      let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogUrl, ogImage, ogType, twitterCard, twitterTitle, twitterDescription]);

  if (!jsonLd || jsonLd.length === 0) return null;

  return (
    <>
      {jsonLd.map((schema, idx) => (
        <script
          key={idx}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}